import { err, ok, Result } from "neverthrow"
import { ExpressionNode } from "./AstNode"
import { CalcScriptCompileErrorMsgs } from "./CompilerTypes"

export type ScopeVariableValue = ExpressionNode | null

class Scope {
	private variables: Map<string, ScopeVariableValue> = new Map()

	constructor(private parent: Scope | null = null) {}

	declareVariable(name: string, value: ScopeVariableValue = null): Result<void, string> {
		if (this.variables.has(name) || this.parent?.variables.has(name)) {
			return err(`Identifier '${name}' has already been declared in the current scope.`)
		}

		this.variables.set(name, value)
		return ok(undefined)
	}

	setVariable(name: string, value: ScopeVariableValue): Result<void, string> {
		if (this.variables.has(name)) {
			this.variables.set(name, value)
			return ok(undefined)
		}

		if (this.parent !== null) {
			return this.parent.setVariable(name, value)
		}

		return err(CalcScriptCompileErrorMsgs.VariableNotDeclared(name))
	}

	getScopeVariable(name: string): Result<ScopeVariableValue|undefined, string> {
		const valueResult = this.getScopeVariableValue(name)

		if (valueResult.isErr()) {
			return err(CalcScriptCompileErrorMsgs.VariableNotDeclared(name))
		}

		return ok(valueResult.value)
	}

	getScopeVariableValue(name: string): Result<ScopeVariableValue|undefined, string> {
		if (this.variables.has(name)) {
			return ok(this.variables.get(name))
		} else if (this.parent !== null) {
			return this.parent.getScopeVariable(name)
		} else {
			return ok(undefined)
		}
	}

	has(name: string): boolean {
		return this.variables.has(name)
	}
}

// "In a compiler system, an environment is a data structure that maps names
// (identifiers) to their meanings — typically their types, values, or memory
// locations — at a given point in a program.
// When the compiler encounters a variable like x, it needs to know: what is x?
// The environment answers that question by associating names with their bindings.
// Environments are typically nested to reflect lexical scope. When you enter a
// new block or function, a new environment frame is pushed on top of the
// old one."
export class CalcScriptCompileEnv {
	private parseTimeVars: Map<string, ScopeVariableValue> = new Map()
	private scopeStack: Scope[] = [new Scope()]

	enterScope() {
		const currentScope = this.getCurrentScope()
		this.scopeStack.push(new Scope(currentScope))
	}

	exitScope() {
		this.scopeStack.pop()
	}

	getCurrentScope(): Scope {
		return this.scopeStack[this.scopeStack.length - 1]
	}

	declareVariable(name: string, value: ScopeVariableValue = null): Result<void, string> {
		// declares in current scope
		const result = this.getCurrentScope().declareVariable(name, value)
		if (result.isErr()) {
			return result
		}
		// only record at parse time if this variable lives at the root scope (global LET)
		if (this.isGlobalScope()) {
			this.parseTimeVars.set(name, value)
		}
		return ok()
	}

	setVariable(name: string, value: ScopeVariableValue): Result<void, string> {
		// this has potential to throw
		const result = this.getCurrentScope().setVariable(name, value)
		if (result.isErr()) {
			return err(result.error)
		}
		// only update parseTimeVars if variable belongs to global scope
		if (this.isGlobalVar(name)) {
			this.parseTimeVars.set(name, value)
		}
		return ok(undefined)
	}

	getScopeVariable(name: string): Result<ScopeVariableValue, string> {
		const result = this.getCurrentScope().getScopeVariable(name)
		if (result.isErr()) {
			return err(result.error)
		}
		if (result.value === undefined) {
			return err(CalcScriptCompileErrorMsgs.VariableNotDeclared(name))
		}
		return ok(result.value)
	}

	getScopeVariableValue(name: string): Result<ScopeVariableValue|undefined, string> {
		const result = this.getCurrentScope().getScopeVariableValue(name)
		if (result.isErr()) {
			return err(result.error)
		}
		return ok(result.value)
	}

	// Parse-time accessor for ConceptCollector
	getScopeExpression(name: string): Result<ExpressionNode, string> {
		const value = this.parseTimeVars.get(name)
		if (value === undefined) {
			return err(`Variable '${name}' not found in global scope`)
		}
		if (value === null) {
			return err(`Variable '${name}' was declared but has no value`)
		}
		return ok(value)
	}

	hasParent(): boolean {
		return this.scopeStack.length > 1
	}

	private isGlobalScope() {
		return this.scopeStack.length === 1
	}

	private isGlobalVar(name: string) {
		return this.scopeStack[0].has(name)
	}
}
